import { v4 as uuidv4 } from "uuid";
import { INTERPRETER_BASE_URL } from "../config/runtime";
import { getSessionId } from "./visualizeService";

const A2A_URL = INTERPRETER_BASE_URL
  ? `${INTERPRETER_BASE_URL}/api/a2a`
  : `/api/a2a`;

// Builds the JSON-RPC body for a Steps method call.
const makeBody = (userId, data) => ({
  jsonrpc: "2.0",
  id: uuidv4(),
  method: "Steps",
  params: {
    metadata: { user_id: userId.toString(), session_id: getSessionId(userId) },
    message: { parts: [{ type: "data", data }] },
  },
});

async function stepsPost(userId, data) {
  const res = await fetch(A2A_URL, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(makeBody(userId, data)),
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  const json = await res.json();
  if (json.error) {
    const err = new Error(json.error.message || "API error");
    err.reason = json.error.data?.reason;
    throw err;
  }
  return json.result?.artifact?.parts?.[0]?.data ?? {};
}

/** Update a step field (name, description, animation). */
export const updateStep = async (
  session_id,
  file_id,
  step_number,
  stepData,
  userId,
  tabId = null,
  action,
  dpm_uuid = null,
) => {
  try {
    const data = await stepsPost(userId, {
      action,
      session_id,
      file_id: file_id?.toString(),
      step_number,
      updated_step_data: stepData,
      tab_id: tabId || null,
      ...(dpm_uuid && { dpm_uuid }),
    });
    if (data.success) return { success: true, dpm_data: data.dpm_data };
    throw new Error("Invalid response format");
  } catch (error) {
    console.error("Failed to update step:", error);
    return { success: false, error: error.message, reason: error.reason };
  }
};

/** Update a single part field within a step. */
export const updateStepPart = async (
  session_id,
  file_id,
  step_number,
  partData,
  userId,
  tabId = null,
  dpm_uuid = null,
) => {
  try {
    const data = await stepsPost(userId, {
      action: "update_part",
      session_id,
      file_id: file_id?.toString(),
      step_number,
      updated_step_data: partData,
      tab_id: tabId || null,
      ...(dpm_uuid && { dpm_uuid }),
    });
    if (data.success) return { success: true };
    throw new Error("Invalid response format");
  } catch (error) {
    console.error("Failed to update step part:", error);
    return { success: false, error: error.message, reason: error.reason };
  }
};

/** Rename a CAD part's display name in `_parts.json` (not step-scoped; gated on DPM draft status). */
export const renamePart = async (
  session_id,
  file_id,
  userId,
  partId,
  name,
  referenceId,
  tabId = null,
  dpm_uuid = null,
) => {
  try {
    const data = await stepsPost(userId, {
      action: "rename_part",
      session_id,
      file_id: file_id?.toString(),
      part_id: partId,
      name,
      reference_id: referenceId,
      tab_id: tabId || null,
      ...(dpm_uuid && { dpm_uuid }),
    });
    if (data.success) return { success: true };
    throw new Error("Invalid response format");
  } catch (error) {
    console.error("Failed to rename part:", error);
    return { success: false, error: error.message, reason: error.reason };
  }
};

/** Append new images to a step. Accepts file_meta IDs (PNG/JPEG) for newly added images only. */
export const addStepImages = async (
  userId,
  session_id,
  file_id,
  step_number,
  step_image_file_ids,
  tabId = null,
  dpm_uuid = null,
) => {
  try {
    const data = await stepsPost(userId, {
      action: "add_step_images",
      session_id,
      file_id: file_id?.toString(),
      step_number,
      step_image_file_ids,
      tab_id: tabId || null,
      ...(dpm_uuid && { dpm_uuid }),
    });
    if (data.success) return { success: true, dpm_data: data.dpm_data };
    return { success: false, error: data.error || "Invalid response" };
  } catch (error) {
    console.error("Failed to add step images:", error);
    return { success: false, error: error.message, reason: error.reason };
  }
};

/** Replace the ordered step_images array. Used for delete and reorder. Sends reference_ids only. */
export const updateStepImages = async (
  userId,
  session_id,
  file_id,
  step_number,
  step_image_reference_ids,
  tabId = null,
  dpm_uuid = null,
) => {
  try {
    const data = await stepsPost(userId, {
      action: "update_step_images",
      session_id,
      file_id: file_id?.toString(),
      step_number,
      step_image_reference_ids,
      tab_id: tabId || null,
      ...(dpm_uuid && { dpm_uuid }),
    });
    if (data.success) return { success: true, dpm_data: data.dpm_data };
    return { success: false, error: data.error || "Invalid response" };
  } catch (error) {
    console.error("Failed to update step images:", error);
    return { success: false, error: error.message, reason: error.reason };
  }
};

/** Persist the step's primary view: gltf or an image in step_images. */
export const setStepPrimary = async (
  userId,
  session_id,
  file_id,
  step_number,
  { reference_type, reference_id = null },
  tabId = null,
  dpm_uuid = null,
) => {
  try {
    const data = await stepsPost(userId, {
      action: "set_step_primary",
      session_id,
      file_id: file_id?.toString(),
      step_number,
      reference_type,
      ...(reference_id != null && { reference_id }),
      tab_id: tabId || null,
      ...(dpm_uuid && { dpm_uuid }),
    });
    if (data.success) return { success: true, dpm_data: data.dpm_data };
    return { success: false, error: data.error || "Invalid response" };
  } catch (error) {
    console.error("Failed to set step primary:", error);
    return { success: false, error: error.message };
  }
};

/** Add a new step at a given 1-based position. */
export const addStep = async (
  session_id,
  file_id,
  placement, // 1-based final position the new step will occupy
  name,
  description = "",
  userId,
  tabId = null,
  dpm_uuid = null,
) => {
  // Placement is the new step's 1-based final position.
  // The API "step_number" means "insert after this position", so subtract 1.
  // e.g. placement=1 (prepend) → step_number=0, placement=3 → step_number=2
  const step_number = placement - 1;
  try {
    const data = await stepsPost(userId, {
      action: "add_step",
      session_id,
      file_id: file_id?.toString(),
      step_number,
      name,
      description,
      tab_id: tabId || null,
      ...(dpm_uuid && { dpm_uuid }),
    });
    if (data.success) {
      return {
        success: true,
        dpm_data: data.dpm_data,
        node_id: data.node_id,
        animation_needs_setup: data.animation_needs_setup ?? true,
      };
    }
    throw new Error("Invalid response format");
  } catch (error) {
    console.error("Failed to add step:", error);
    return { success: false, error: error.message, reason: error.reason };
  }
};

/** Delete a step by its 1-based step number. */
export const deleteStep = async (
  session_id,
  file_id,
  step_number,
  userId,
  tabId = null,
  dpm_uuid = null,
) => {
  try {
    const data = await stepsPost(userId, {
      action: "delete_step",
      session_id,
      file_id: file_id?.toString(),
      step_number,
      tab_id: tabId || null,
      ...(dpm_uuid && { dpm_uuid }),
    });
    if (data.success) {
      return {
        success: true,
        dpm_data: data.dpm_data,
        deleted_node_id: data.deleted_node_id,
      };
    }
    throw new Error("Invalid response format");
  } catch (error) {
    console.error("Failed to delete step:", error);
    return { success: false, error: error.message, reason: error.reason };
  }
};

/** Move a step from one 1-based position to another. */
export const reorderStep = async (
  session_id,
  file_id,
  step_number,
  new_step_number,
  userId,
  tabId = null,
  dpm_uuid = null,
) => {
  try {
    const data = await stepsPost(userId, {
      action: "reorder_step",
      session_id,
      file_id: file_id?.toString(),
      step_number,
      new_step_number,
      tab_id: tabId || null,
      ...(dpm_uuid && { dpm_uuid }),
    });
    if (data.success) {
      return {
        success: true,
        dpm_data: data.dpm_data,
        node_id: data.node_id,
      };
    }
    throw new Error("Invalid response format");
  } catch (error) {
    console.error("Failed to reorder step:", error);
    return { success: false, error: error.message, reason: error.reason };
  }
};

/**
 * Replace source file links for a step.
 * Accepts file_meta integer IDs; backend resolves them to chunk UUIDs.
 */
export const updateSources = async (
  userId,
  session_id,
  file_id,
  step_number,
  source_file_ids,
  tabId = null,
  dpm_uuid = null,
) => {
  try {
    const data = await stepsPost(userId, {
      action: "update_sources",
      session_id,
      file_id: file_id?.toString(),
      step_number,
      source_file_ids,
      tab_id: tabId || null,
      ...(dpm_uuid && { dpm_uuid }),
    });
    if (data.success) return { success: true, dpm_data: data.dpm_data };
    return { success: false, error: data.error || "Invalid response" };
  } catch (error) {
    console.error("Failed to update sources:", error);
    return { success: false, error: error.message, reason: error.reason };
  }
};
